import { ApolloError } from "@apollo/client";
import { Box, Button, Typography } from "@mui/material";
import { useCurrentUser } from "hooks/useCurrentUser";
import { isAuthorizationError } from "../utils/errorUtils";
import { StackTraceErrorBox } from "./StackTraceErrorBox";
import { Unauthorized } from "./Unauthorized";

interface ErrorDisplayProps {
  error?: Error;
  apolloError?: ApolloError;
  compact?: boolean;
  onAction?: () => void;
  actionLabel?: string;
  mainText?: string;
  subText?: string;
}

export const ErrorDisplay = ({
  error,
  apolloError,
  compact = false,
  mainText = "We've encountered an error",
  subText = "Your error has been reported to Encamp. Please reach out to Encamp Support if it persists.",
  onAction,
  actionLabel = "Go Back",
}: ErrorDisplayProps) => {
  const { isStaff } = useCurrentUser();
  const showTrace = isStaff || !import.meta.env.PROD;
  const msg = error?.stack || error?.message;

  // Check if this is an authorization error and show the Unauthorized component
  if (error && isAuthorizationError(error)) {
    return <Unauthorized />;
  }

  const imageSize = compact
    ? {
        height: "120px",
        maxWidth: "145px",
      }
    : {
        height: "209px",
        maxWidth: "252px",
      };

  const textStyles = compact
    ? {
        title: {
          fontSize: "24px",
          fontWeight: "300",
          lineHeight: "32px",
          mb: 1,
        },
        subtitle: {
          fontSize: "16px",
          fontWeight: "400",
          lineHeight: "24px",
          mb: 2,
        },
      }
    : {
        title: {
          fontSize: "60px",
          fontWeight: "300",
          lineHeight: "72px",
          mb: 2,
        },
        subtitle: {
          fontSize: "24px",
          fontWeight: "400",
          lineHeight: "32px",
          mb: 3,
        },
      };

  const spacing = compact
    ? {
        container: { px: 4, py: 3 },
        content: { pl: 4 },
      }
    : {
        container: { mt: "144px", mb: 7 },
        content: { pl: 9 },
      };

  return (
    <>
      <Box display="flex" flexDirection="row" sx={spacing.container}>
        <img src="/oops_hiker.png" alt="An error occurred" style={imageSize} />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={spacing.content}
        >
          <Typography
            sx={{
              textAlign: "left",
              textWrap: "pretty",
              ...textStyles.title,
            }}
          >
            {mainText}
          </Typography>
          <Typography
            sx={{
              textAlign: "left",
              textWrap: "pretty",
              ...textStyles.subtitle,
            }}
          >
            {subText}
          </Typography>
          {onAction && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button variant="text" type="button" onClick={onAction}>
                {actionLabel}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {showTrace && error && (
        <Box sx={{ px: compact ? 2 : 0 }}>
          <StackTraceErrorBox
            msg={msg}
            error={error}
            apolloError={apolloError}
          />
        </Box>
      )}
    </>
  );
};
